import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { useAuth, formatApiError } from "@/contexts/AuthContext";

export default function ForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="bg-[#050505] text-white pt-32 min-h-screen">
      <div className="px-6 md:px-12 lg:px-24 pb-24 max-w-md mx-auto">
        <p className="eyebrow mb-4">Reset password</p>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mb-2">
          Forgot your password?
        </h1>
        <p className="text-zinc-400 mb-12 text-sm">
          Enter your email and we'll send you a link to set a new one.
        </p>

        {sent ? (
          <div
            data-testid="forgot-password-sent-state"
            className="border border-[#E8A851]/40 bg-gradient-to-br from-[#E8A851]/10 to-transparent p-8"
          >
            <CheckCircle2 className="w-10 h-10 text-[#E8A851] mb-4" />
            <p className="font-display text-2xl font-medium mb-3">Check your inbox.</p>
            <p className="text-zinc-300 text-sm leading-relaxed">
              If an account exists for <span className="text-white">{email}</span>, we've sent
              a reset link. The link is good for one hour.
            </p>
            <Link to="/login" data-testid="forgot-back-to-login" className="btn-secondary mt-8 inline-flex !border-white/20">
              Back to sign in <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/10 bg-white/[0.02] p-8 space-y-6">
            <div>
              <label className="eyebrow mb-2 block">Email</label>
              <input
                data-testid="forgot-password-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@church.org"
                className="w-full bg-transparent border-b border-white/20 focus:border-white text-white px-0 py-3 focus:outline-none"
              />
            </div>
            {err && <p data-testid="forgot-password-error" className="text-sm text-red-400">{err}</p>}
            <button
              data-testid="forgot-password-submit-button"
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-50"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send reset link</>}
            </button>
          </form>
        )}

        <p className="text-sm text-zinc-500 mt-8 text-center">
          <Link to="/login" data-testid="forgot-to-login-link" className="text-white underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
